import { useQuery } from "@tanstack/react-query";
import { useNavigate, useLocation } from "react-router-dom";
import { clearAuthStorage } from "@/utils";
import apiClient from "@/services/apiClient";

/**
 * React Query-backed GET hook with caching.
 * Returns the full parsed response data so callers
 * can access top-level fields beyond `data` (e.g. `has_ga_distribution`).
 *
 * Returns [fullResponseJson, isLoaded, error, refetch, isFetching].
 *   - isLoaded: false only on the very first fetch (no cached data yet). Safe to
 *     gate loading spinners on this — background refetches leave it true.
 *   - isFetching: true whenever any network request is in-flight, including
 *     background refetches. Use this for subtle "refreshing" indicators only.
 * Pass url=null to disable the query.
 */
const useGetQuery = (url, options = {}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const queryFn = async () => {
    try {
      const response = await apiClient.get(url, {
        headers: options.headers,
      });
      return response.data;
    } catch (err) {
      if (err.response?.status === 401) {
        clearAuthStorage();
        localStorage.setItem(
          "redirectAfterLogin",
          location.pathname + location.search,
        );
        navigate("/login");
        return null;
      }
      const errorMessage =
        err.response?.data?.message ||
        (err.response?.statusText
          ? `Failed to fetch data: ${err.response.statusText}`
          : err.response?.status
            ? `HTTP error! Status: ${err.response.status}`
            : err.message);
      throw new Error(errorMessage);
    }
  };

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: [url],
    queryFn,
    enabled: !options.skip && !!url,
    staleTime: options.staleTime ?? 5 * 60 * 1000, // default: 5 minutes
  });

  const isLoaded = !isLoading && data !== undefined;

  return [data ?? null, isLoaded, error ?? null, refetch, isFetching];
};

export default useGetQuery;
